/**
 * skyEmployee in-app notifications (the bell), shared by client and server.
 * In-app only (decision P3-8): no email. Every category starts on; each
 * person can turn a category off, except moderation outcomes about their own
 * posts and reports, which always arrive.
 */

/** What happened. Stored in emp_notifications.type. */
const EMP_NOTIFICATION_TYPES = [
  "mention", // @mentioned in a post or comment
  "comment", // a comment on my post
  "reply", // a reply to my comment
  "like", // a like on my post or comment
  "shout_out", // a shout-out naming me
  "ecard", // an e-card sent to me
  "milestone", // a colleague's milestone today (Managers and Admins)
  "announcement", // an announcement aimed at me
  "poll", // a poll aimed at me
  "survey", // a survey aimed at me has opened, or is closing soon
  "message", // someone sent me a private message
  "post_hidden", // a moderator hid my post
  "post_removed", // a moderator removed my post or comment
  "report_outcome", // the report I made was reviewed
  "giftcard_codes", // another Admin loaded or voided gift card codes (P5-4)
  "gift_awarded", // I was given a gift card or reward
  "gift_approval", // a Manager's award is waiting for approval (Admins)
  "gift_low_stock", // a reward type's codes or stock are running low (Admins)
  "gift_decided", // an Admin approved or declined my award (Managers)
  "gift_expiring", // a gift card code I haven't revealed expires within 14 days
  "gift_ready", // a reward I was given is ready to pick up
  "gift_cancelled", // a reward I was given was cancelled
  "resource_ack", // a policy (or a new version of one) needs my acknowledgement
  "resource_ack_reminder", // a reminder to acknowledge a policy
  "resource_review", // a resource I own is past its review date
  "broadcast", // a broadcast from my organisation arrived
  "broadcast_approval", // a broadcast is waiting for me to approve it
  "broadcast_decided", // my broadcast was approved, declined or withdrawn
  "broadcast_reply_in", // someone replied to a broadcast, in a thread I read
  "broadcast_reply", // the organisation answered my reply to a broadcast
  "automation_notice", // an automation tells me something about one of my people
  "automation_paused", // my automation stopped (its owner can no longer send, or it kept failing)
  "texting_paused", // this month's texting budget is spent: texts wait (Admins)
] as const;
export type EmpNotificationType = (typeof EMP_NOTIFICATION_TYPES)[number];

/** The switches on the Notifications settings page. */
export const EMP_NOTIFICATION_CATEGORIES = [
  "mentions",
  "comments",
  "likes",
  "shout_outs",
  "announcements",
  "milestones",
  "gift_cards",
  "acknowledgements",
  // Its own switch rather than folded into announcements: someone who mutes
  // company news may still want to be asked for their opinion, and a survey
  // nobody is reminded about is a survey nobody answers.
  "surveys",
  // Its own switch, and one most people will want left on: a message is
  // addressed to you personally, unlike anything else in this list.
  "messages",
  // Broadcasts from the organisation, when they arrived in Messages. They go
  // by text and email now; kept so earlier settings and notifications read.
  "organisation",
] as const;
export type EmpNotificationCategory = (typeof EMP_NOTIFICATION_CATEGORIES)[number];

/** Each type's switch; null means it can't be turned off. */
export const NOTIFICATION_CATEGORY_OF: Record<EmpNotificationType, EmpNotificationCategory | null> =
  {
    mention: "mentions",
    comment: "comments",
    reply: "comments",
    like: "likes",
    shout_out: "shout_outs",
    ecard: "shout_outs",
    milestone: "milestones",
    giftcard_codes: null, // every load and void reaches the other Admins (P5-4)
    gift_awarded: "gift_cards",
    gift_approval: "gift_cards",
    gift_low_stock: "gift_cards",
    gift_decided: "gift_cards",
    gift_expiring: "gift_cards",
    gift_ready: "gift_cards",
    gift_cancelled: "gift_cards",
    resource_ack: "acknowledgements",
    resource_ack_reminder: "acknowledgements",
    resource_review: "acknowledgements",
    announcement: "announcements",
    survey: "surveys",
    message: "messages",
    poll: "announcements",
    broadcast: "organisation",
    broadcast_approval: null, // a request waiting on me always arrives
    broadcast_decided: null, // so does the answer to mine
    broadcast_reply_in: null, // a reply waiting on me is work, like an approval
    broadcast_reply: null, // an answer to my own reply, even with the channel muted
    automation_notice: null, // work about my people, like an approval
    automation_paused: null, // about my own automation
    texting_paused: null, // texts aren't going out: Admins always hear
    post_hidden: null,
    post_removed: null,
    report_outcome: null,
  };

export const NOTIFICATION_CATEGORY_LABELS: Record<EmpNotificationCategory, string> = {
  mentions: "Mentions",
  comments: "Comments and replies",
  likes: "Likes",
  shout_outs: "Shout-outs and cards",
  announcements: "Announcements and polls",
  milestones: "Milestones",
  gift_cards: "Gift cards and rewards",
  acknowledgements: "Policies and acknowledgements",
  surveys: "Surveys",
  messages: "Messages",
  organisation: "Messages from your organisation",
};

export interface EmpNotificationPrefs {
  /** Categories turned off. Empty by default: everything on. */
  mutedCategories: EmpNotificationCategory[];
  quietHours: {
    enabled: boolean;
    /** Hour of day, 0–23, in `timeZone`. */
    from: number;
    to: number;
  };
  /** IANA time zone, from the person's browser; null until first saved. */
  timeZone: string | null;
}

export const DEFAULT_NOTIFICATION_PREFS: EmpNotificationPrefs = {
  mutedCategories: [],
  quietHours: { enabled: true, from: 22, to: 6 },
  timeZone: null,
};

/** One row in the bell. */
export interface EmpNotification {
  id: string;
  type: EmpNotificationType;
  /** Who did it; null for system events (a moderator is never named). */
  actor: { partyId: string; name: string; photoUrl: string | null } | null;
  /** What it's about ("post", "comment", …), for the wording. */
  entityType: string | null;
  /** A short piece of the post or comment, as plain text. */
  snippet: string | null;
  /** In-app path to open, relative to skyEmployee. */
  link: string | null;
  read: boolean;
  createdAt: string;
}

export interface EmpNotificationPage {
  items: EmpNotification[];
  /** Pass back as `before` for the next page; null when there is none. */
  nextCursor: string | null;
}

export const NOTIFICATION_PAGE_SIZE = 20;
/** The bell shows "99+" beyond this. */
export const UNREAD_COUNT_CAP = 99;
export const NOTIFICATION_SNIPPET_MAX = 140;
